import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUp, Sparkles, Shield, Lock, Eye, FileText, CheckCircle, ChevronDown, Building2, Briefcase, Calculator, Globe, Server, Clock, Mail, Zap, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { cognitoAuth } from '@/services/auth/cognito';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

function AnimatedBar({ value, color, delay }: { value: number; color: string; delay: number }) {
  const { ref, inView } = useInView();
  const hoverValue = Math.min(value + 10, 100);
  return (
    <div ref={ref} className="flex-1 group/bar cursor-pointer relative" style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}>
      <div
        className="w-full transition-all ease-out relative"
        style={{
          height: inView ? `${value}%` : '0%',
          backgroundColor: color,
          transitionDuration: '1200ms',
          transitionDelay: `${delay}ms`,
          boxShadow: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.height = `${hoverValue}%`;
          e.currentTarget.style.transitionDuration = '300ms';
          e.currentTarget.style.transitionDelay = '0ms';
          e.currentTarget.style.boxShadow = '0 0 12px 4px rgba(80,72,229,0.4), 0 0 24px 8px rgba(80,72,229,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.height = `${value}%`;
          e.currentTarget.style.transitionDuration = '300ms';
          e.currentTarget.style.transitionDelay = '0ms';
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
    </div>
  );
}

const HERO_TAGLINES = [
  'Talk to your documents.',
  'Talk to your client files.',
  'Talk to Xero, in plain English.',
  'Talk to ATO guidance.',
  'Talk to ASIC filings.',
  'Talk to all of it. At once.',
];

/* ─── Feature Showcase Carousel ─── */
function FeatureShowcase() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = 3;
  const touchStart = useRef(0);

  // Auto-advance (pauses on hover)
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive(p => (p + 1) % total), 12000);
    return () => clearInterval(t);
  }, [paused]);

  const handleDrag = (startX: number, endX: number) => {
    const diff = startX - endX;
    if (Math.abs(diff) > 50) {
      setActive(p => diff > 0 ? (p + 1) % total : (p - 1 + total) % total);
    }
  };

  return (
    <div className="relative select-none">
      <div
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onMouseDown={e => { touchStart.current = e.clientX; }}
        onMouseUp={e => handleDrag(touchStart.current, e.clientX)}
        onTouchStart={e => { touchStart.current = e.touches[0].clientX; }}
        onTouchEnd={e => handleDrag(touchStart.current, e.changedTouches[0].clientX)}
      >
        <div className="flex items-stretch transition-transform duration-700 ease-in-out" style={{ transform: `translateX(-${active * 100}%)` }}>
          {/* ── Slide 1: ATO Compliance with Chart ── */}
          <div className="w-full flex-shrink-0">
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6 space-y-5 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs font-medium text-zinc-300">ATO Compliance Analysis: Chen & Associates</span>
                </div>
                <span className="text-[10px] text-zinc-600">FY 2024–25</span>
              </div>
              <div>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-3">Revenue vs Industry Benchmark</p>
                <div className="flex gap-3" style={{ height: '120px' }}>
                  {[
                    { label: 'Q1', value: 72, bench: 65 },
                    { label: 'Q2', value: 58, bench: 65 },
                    { label: 'Q3', value: 85, bench: 68 },
                    { label: 'Q4', value: 91, bench: 70 },
                  ].map((q, i) => (
                    <div key={q.label} className="flex-1 flex flex-col">
                      <div className="flex-1 flex gap-1 items-end">
                        <AnimatedBar value={active === 0 ? q.value : 0} color="rgba(80,72,229,0.5)" delay={i * 200} />
                        <AnimatedBar value={active === 0 ? q.bench : 0} color="rgba(113,113,122,0.35)" delay={i * 200 + 100} />
                      </div>
                      <span className="text-center text-[9px] text-zinc-600 mt-1.5">{q.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-[#5048e5]/60" /><span className="text-[9px] text-zinc-500">Client</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-zinc-600/50" /><span className="text-[9px] text-zinc-500">Industry Avg</span></div>
                </div>
              </div>
              <div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-zinc-800/60">
                    <th className="text-left py-2 text-zinc-500 font-medium">Metric</th>
                    <th className="text-right py-2 text-zinc-500 font-medium">Client</th>
                    <th className="text-right py-2 text-zinc-500 font-medium">Benchmark</th>
                    <th className="text-right py-2 text-zinc-500 font-medium">Status</th>
                  </tr></thead>
                  <tbody>
                    {[
                      { m: 'Cost of Sales', c: '62.4%', b: '58–65%', s: 'ok' },
                      { m: 'Labour / Revenue', c: '38.1%', b: '28–35%', s: 'warn' },
                      { m: 'Motor Vehicle', c: '4.7%', b: '2–4%', s: 'warn' },
                      { m: 'Net Profit Margin', c: '14.3%', b: '12–18%', s: 'ok' },
                    ].map(r => (
                      <tr key={r.m} className="border-b border-zinc-800/30 transition-all duration-300 hover:bg-[#5048e5]/[0.06] hover:shadow-[inset_0_0_20px_rgba(80,72,229,0.08)] cursor-default">
                        <td className="py-1.5 text-zinc-300">{r.m}</td>
                        <td className="py-1.5 text-right text-zinc-300">{r.c}</td>
                        <td className="py-1.5 text-right text-zinc-500">{r.b}</td>
                        <td className="py-1.5 text-right"><span className={cn('text-[10px] px-1.5 py-0.5', r.s === 'ok' ? 'text-green-400 bg-green-400/10' : 'text-amber-400 bg-amber-400/10')}>{r.s === 'ok' ? 'Within Range' : 'Above Range'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-zinc-800/40 pt-3">
                <div className="flex items-start gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-[#7c75f0] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-zinc-400 leading-relaxed"><span className="text-zinc-300 font-medium">AI Summary:</span> Labour costs and motor vehicle expenses exceed ATO benchmarks. Consider reviewing contractor arrangements and logbook substantiation.</p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Slide 2: AI Chat with Citations ── */}
          <div className="w-full flex-shrink-0">
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6 space-y-4 h-full flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-[#5048e5] animate-pulse" />
                <span className="text-xs font-medium text-zinc-300">AI Document Chat: Multi-Client Query</span>
              </div>

              {/* Chat messages */}
              <div className="space-y-4 flex-1">
                {/* AI response */}
                <div className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#5048e5]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="h-3.5 w-3.5 text-[#7c75f0]" />
                  </div>
                  <div className="space-y-2 flex-1">
                    <p className="text-[12px] text-zinc-300 leading-relaxed">Based on the uploaded tax returns and Xero data, <span className="text-white font-medium">3 clients</span> have unclaimed deductions totalling <span className="text-green-400 font-medium">$47,200</span>:</p>
                    <div className="space-y-1.5">
                      {[
                        { client: 'Chen & Associates', amount: '$18,400', type: 'Home office & depreciation' },
                        { client: 'Torres Advisory', amount: '$21,300', type: 'Vehicle logbook shortfall' },
                        { client: 'Sharma Partners', amount: '$7,500', type: 'Professional development' },
                      ].map(item => (
                        <div key={item.client} className="flex items-center gap-2 p-2 bg-zinc-800/30 border border-zinc-800/40 transition-all duration-300 hover:bg-[#5048e5]/[0.06] hover:border-[#5048e5]/20 cursor-default">
                          <CheckCircle className="h-3 w-3 text-green-400 flex-shrink-0" />
                          <span className="text-[11px] text-zinc-300 font-medium">{item.client}</span>
                          <span className="text-[11px] text-green-400 ml-auto">{item.amount}</span>
                          <span className="text-[10px] text-zinc-600 hidden sm:block">— {item.type}</span>
                        </div>
                      ))}
                    </div>
                    {/* Citations */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {['Tax_Return_2025_Chen.pdf', 'Xero P&L: Torres', 'FY25_Deductions_Log.xlsx'].map(src => (
                        <span key={src} className="text-[9px] px-2 py-0.5 bg-[#5048e5]/10 border border-[#5048e5]/20 text-[#9c86fd] transition-all duration-300 hover:bg-[#5048e5]/20 cursor-default">{src}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Prompt input */}
              <div className="border-t border-zinc-800/40 pt-4 mt-2">
                <div className="flex items-center gap-3 p-3 bg-zinc-800/20 border border-zinc-800/40 transition-all duration-300 hover:border-[#5048e5]/20">
                  <span className="text-[12px] text-zinc-600 flex-1 italic">Which clients have missed deductions this year<span className="inline-block w-[2px] h-[14px] bg-[#5048e5] ml-0.5 align-middle animate-[blink_1s_step-end_infinite]" /></span>
                  <div className="w-8 h-8 rounded-lg bg-[#5048e5] flex items-center justify-center flex-shrink-0 transition-all duration-300 hover:shadow-[0_0_16px_rgba(80,72,229,0.5)]">
                    <ArrowUp className="h-3.5 w-3.5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Slide 3: Tax Planning Scenarios ── */}
          <div className="w-full flex-shrink-0">
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6 space-y-4 h-full flex flex-col">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-xs font-medium text-zinc-300">Year-End Tax Planning: Torres Advisory</span>
                </div>
                <span className="text-[10px] text-zinc-600">FY 2024–25</span>
              </div>

              {/* Scenario cards */}
              <div className="space-y-2">
                {[
                  { action: 'Prepay Expenses', saving: '$12,400', deadline: '30 Jun', priority: 'High', color: 'text-red-400 bg-red-400/10' },
                  { action: 'Super Contribution Top-Up', saving: '$8,750', deadline: '28 Jun', priority: 'High', color: 'text-red-400 bg-red-400/10' },
                  { action: 'Asset Write-Off (< $20k)', saving: '$6,200', deadline: '30 Jun', priority: 'Medium', color: 'text-amber-400 bg-amber-400/10' },
                  { action: 'Bad Debt Write-Off', saving: '$3,800', deadline: '30 Jun', priority: 'Medium', color: 'text-amber-400 bg-amber-400/10' },
                  { action: 'Income Deferral Strategy', saving: '$15,600', deadline: '25 Jun', priority: 'High', color: 'text-red-400 bg-red-400/10' },
                ].map(s => (
                  <div key={s.action} className="flex items-center gap-3 p-2.5 bg-zinc-800/20 border border-zinc-800/40 transition-all duration-300 hover:bg-[#5048e5]/[0.06] hover:border-[#5048e5]/20 cursor-default">
                    <div className="flex-1">
                      <p className="text-[12px] text-zinc-300 font-medium">{s.action}</p>
                      <p className="text-[10px] text-zinc-600">Deadline: {s.deadline}</p>
                    </div>
                    <span className="text-[12px] text-green-400 font-medium">{s.saving}</span>
                    <span className={cn('text-[9px] px-1.5 py-0.5', s.color)}>{s.priority}</span>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="border-t border-zinc-800/40 pt-3 flex items-center justify-between">
                <span className="text-xs text-zinc-500">Total estimated tax saving</span>
                <span className="text-lg font-medium text-green-400">$46,750</span>
              </div>

              <div className="flex items-start gap-2 pt-1">
                <Sparkles className="h-3.5 w-3.5 text-[#7c75f0] mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-zinc-400 leading-relaxed"><span className="text-zinc-300 font-medium">Recommendation:</span> Prioritise super top-up and prepaid expenses before 28 June. Income deferral requires client approval, schedule advisory call.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2 mt-5">
        {Array.from({ length: total }).map((_, i) => (
          <button key={i} onClick={() => setActive(i)} className={cn(
            'w-2 h-2 rounded-full transition-all duration-500',
            active === i ? 'bg-[#5048e5] w-6' : 'bg-zinc-700 hover:bg-zinc-500'
          )} />
        ))}
      </div>

      <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#5048e5]/10 rounded-full blur-[80px] pointer-events-none" />
    </div>
  );
}

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [currentTagline, setCurrentTagline] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = () => {
    cognitoAuth.signOut();
    logout();
    window.location.href = '/login';
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTagline(prev => (prev + 1) % HERO_TAGLINES.length), 4500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="bg-[#262635] text-white min-h-screen">

      {/* ===== TOP BAR ===== */}
      <header className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
        scrolled && !menuOpen ? 'bg-[#262635]/90 backdrop-blur-md' : ''
      )}>
        <div className="flex items-center justify-between px-6 md:px-12 h-20">
          <Link to="/" className="flex items-center gap-2 z-50">
            <Sparkles className="h-5 w-5 text-[#7c75f0]" />
            <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
          </Link>

          <div className="flex items-center gap-4 md:gap-6 z-50">
            {/* Workshops — secondary link */}
            <Link
              to="/workshops"
              className="hidden md:block text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition"
            >
              Workshops
            </Link>
            {/* Book Demo — primary CTA, always visible */}
            <Link
              to="/demo"
              className="inline-flex items-center gap-1.5 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[10px] md:text-xs tracking-[0.15em] uppercase px-3 md:px-5 py-2 md:py-2.5 rounded-full transition-all duration-300"
            >
              Book Demo
              <ArrowRight className="h-3 w-3 md:h-3.5 md:w-3.5" />
            </Link>
            {isAuthenticated ? (
              <>
                <Link to="/panel" className="hidden md:block text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
                  Go to Panel
                </Link>
                <button onClick={handleLogout} className="hidden md:flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
                  <LogOut className="h-3.5 w-3.5" /> Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="hidden md:block text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
                Client Sign In
              </Link>
            )}
            <button onClick={() => setMenuOpen(!menuOpen)} className="flex items-center gap-3 group">
              <span className="text-xs tracking-[0.15em] uppercase text-zinc-400 group-hover:text-white transition hidden md:block">
                {menuOpen ? 'Close' : 'Menu'}
              </span>
              <div className="w-8 h-8 flex flex-col justify-center items-center gap-1.5">
                <span className={cn('block w-6 h-[1.5px] bg-white transition-all duration-300', menuOpen && 'rotate-45 translate-y-[3.75px]')} />
                <span className={cn('block w-6 h-[1.5px] bg-white transition-all duration-300', menuOpen && '-rotate-45 -translate-y-[3.75px]')} />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* ===== FULLSCREEN SLIDING MENU ===== */}
      <div className={cn(
        'fixed inset-0 z-40 bg-[#262635] transition-all duration-700 ease-in-out flex flex-col justify-center',
        menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      )}>
        <nav className="px-12 md:px-24">
          <ul className="space-y-2">
            {[
              { label: 'What We Do', id: 'what-we-do' },
              { label: 'How It Works', id: 'how-it-works' },
              { label: 'Capabilities', id: 'capabilities' },
              { label: 'Who It\'s For', id: 'who-its-for' },
              { label: 'Security', id: 'security' },
              { label: 'Pricing', id: 'pricing' },
              { label: 'FAQ', id: 'faq' },
              { label: 'Contact', id: 'contact' },
            ].map((item, i) => (
              <li key={item.id} className={cn(
                'transition-all duration-500',
                menuOpen ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8',
              )} style={{ transitionDelay: menuOpen ? `${i * 80}ms` : '0ms' }}>
                <button
                  onClick={() => scrollTo(item.id)}
                  className="text-4xl md:text-6xl font-extralight text-zinc-400 hover:text-white transition-colors duration-300 leading-tight py-1"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          <div className={cn(
            'mt-12 pt-8 border-t border-zinc-800 flex flex-col md:flex-row gap-6 transition-all duration-500',
            menuOpen ? 'opacity-100' : 'opacity-0'
          )} style={{ transitionDelay: menuOpen ? '600ms' : '0ms' }}>
            {isAuthenticated ? (
              <>
                <Link to="/panel" onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-[#7c75f0] hover:text-[#6b64ef] transition">
                  Go to Panel →
                </Link>
                <button onClick={() => { setMenuOpen(false); handleLogout(); }}
                  className="text-sm tracking-[0.15em] uppercase text-zinc-500 hover:text-white transition text-left">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/demo" onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-[#7c75f0] hover:text-[#6b64ef] transition">
                  Book Demo →
                </Link>
                <Link to="/workshops" onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
                  Workshops
                </Link>
                <Link to="/signup" onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
                  Start Free Trial
                </Link>
                <Link to="/login" onClick={() => setMenuOpen(false)}
                  className="text-sm tracking-[0.15em] uppercase text-zinc-500 hover:text-white transition">
                  Client Sign In
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>

      {/* ===== HERO ===== */}
      <section className="h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1040] via-[#0f0e1a] to-[#1a0f2a]" />
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#5048e5]/15 rounded-full blur-[180px]" />
          <div className="absolute bottom-1/4 right-1/5 w-[500px] h-[500px] bg-[#7c3aed]/20 rounded-full blur-[150px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] h-[450px] bg-[#a78bfa]/12 rounded-full blur-[130px]" />
          <div className="absolute top-[15%] right-[20%] w-[350px] h-[350px] bg-[#8b5cf6]/14 rounded-full blur-[140px]" />
        </div>

        <div className="relative z-10 text-center px-6 w-full max-w-6xl">
          <p className="text-[11px] md:text-[13px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">
            <span className="text-white/90 font-medium">RoniDoc</span> · AI for Australian Accounting Firms
          </p>

          {/* Static anchor headline — what RoniDoc IS. */}
          <h1 className="text-[clamp(2.25rem,7.5vw,6.5rem)] font-extralight leading-[1.05] tracking-tight">
            The AI brain for your firm.
          </h1>

          {/* Sliding subhead — what you can DO with it. */}
          <h2
            className="text-[clamp(2rem,7vw,6rem)] font-bold leading-[1.1] tracking-tight relative overflow-hidden mx-auto mt-1"
            style={{ minHeight: '1.3em' }}
          >
            {HERO_TAGLINES.map((tag, i) => (
              <span
                key={i}
                className={cn(
                  'absolute inset-x-0 top-0 transition-all duration-1000 ease-in-out',
                  currentTagline === i ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-[0.4em]'
                )}
              >
                <span className="bg-gradient-to-br from-white via-white to-[#bdb4ff] bg-clip-text text-transparent">
                  {tag}
                </span>
              </span>
            ))}
          </h2>

          <p className="mt-8 text-sm md:text-base text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            Start talking to your <span className="text-white font-medium">documents, client files, accounting software, ATO and ASIC</span> — all connected, and ready to answer in seconds.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/signup"
              className="inline-flex items-center gap-2 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[12px] tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all duration-300">
              Start Free Trial
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={() => scrollTo('what-we-do')}
              className="text-[12px] tracking-[0.15em] uppercase text-zinc-400 hover:text-white py-4 px-8 border border-zinc-800 rounded-full hover:border-zinc-600 transition-all duration-300">
              Learn More
            </button>
          </div>
        </div>

        <button onClick={() => scrollTo('what-we-do')}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 w-[1px] h-16 bg-gradient-to-b from-transparent to-zinc-600 animate-pulse" />
      </section>

      {/* ===== WHAT WE DO ===== */}
      <section id="what-we-do" className="px-6 md:px-12 lg:px-24 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-10">What We Do</p>
            <h2 className="text-[clamp(1.5rem,4vw,3.5rem)] font-extralight leading-[1.3]">
              RoniDoc AI transforms how accounting firms interact with client data.
              <span className="text-white font-normal"> Every document, every Xero report, every ATO regulation </span>
              connected through intelligence that understands your practice.
            </h2>
          </div>
          <FeatureShowcase />
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">How It Works</p>
        <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-20">
          From signup to insights in <span className="font-normal">under five minutes.</span>
        </h2>

        <div className="space-y-24">
          {/* Step 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[3.5rem] font-extralight text-[#7f68f9]/30">01</span>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-[#5048e5]/20 to-transparent" />
              </div>
              <h3 className="text-xl font-medium mb-4">Connect your Xero account</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">One-click OAuth connection. Select which client organisations to connect. ABN auto-matching links your local clients to their Xero data instantly. No manual mapping needed.</p>
            </div>
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#13B5EA]/20 flex items-center justify-center">
                  <span className="text-[#13B5EA] text-lg font-bold">X</span>
                </div>
                <div>
                  <div className="text-sm font-medium">Xero Connected</div>
                  <div className="text-[10px] text-green-400">3 organisations synced</div>
                </div>
              </div>
              {['Azad Accounting Pty Ltd', 'Torres Advisory Group', 'Chen & Associates'].map((org, i) => (
                <div key={i} className="flex items-center gap-2 py-2 border-t border-zinc-800/40">
                  <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-xs text-zinc-400">{org}</span>
                  <span className="ml-auto text-[10px] text-zinc-600">Synced</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="md:order-2">
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[3.5rem] font-extralight text-[#7f68f9]/30">02</span>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-[#5048e5]/20 to-transparent" />
              </div>
              <h3 className="text-xl font-medium mb-4">Upload documents & ask questions</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">Drag and drop tax returns, financial statements, or any accounting document. AI indexes everything and makes it searchable through natural language. Just ask a question in plain English.</p>
            </div>
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6 md:order-1">
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/30 border border-zinc-800/40">
                  <FileText className="h-4 w-4 text-[#7c75f0]" />
                  <span className="text-xs text-zinc-300">Tax_Return_2025_Chen.pdf</span>
                  <span className="ml-auto text-[10px] text-green-400">Indexed</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/30 border border-zinc-800/40">
                  <FileText className="h-4 w-4 text-[#7c75f0]" />
                  <span className="text-xs text-zinc-300">Balance_Sheet_Q4.xlsx</span>
                  <span className="ml-auto text-[10px] text-green-400">Indexed</span>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-[#5048e5]/10 border border-[#5048e5]/20">
                  <p className="text-xs text-zinc-400 italic">"What deductions did Chen claim last year that they haven't claimed this year?"</p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <span className="text-[3.5rem] font-extralight text-[#7f68f9]/30">03</span>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-[#5048e5]/20 to-transparent" />
              </div>
              <h3 className="text-xl font-medium mb-4">Get actionable insights instantly</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">Compliance alerts, tax opportunities, and risk assessments generated from live data. Export branded reports in PDF, Word, or Excel, ready for your clients.</p>
            </div>
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-6 space-y-3">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-xs text-zinc-300">$47,200 in unclaimed deductions found</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <Shield className="h-4 w-4 text-amber-400" />
                <span className="text-xs text-zinc-300">2 clients flagged: high audit risk</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-[#5048e5]/10 border border-[#5048e5]/20">
                <FileText className="h-4 w-4 text-[#7c75f0]" />
                <span className="text-xs text-zinc-300">Year-end tax plan generated for 12 clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOURS TO SECONDS ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#5048e5]/[0.04] via-transparent to-[#5048e5]/[0.04]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#5048e5]/[0.06] rounded-full blur-[200px]" />
        </div>

        <div className="relative z-10">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">The Difference</p>
            <h2 className="text-[clamp(1.8rem,4vw,3.5rem)] font-extralight leading-tight mb-6">
              What takes <span className="font-bold text-amber-400">hours</span> now takes <span className="font-bold text-green-400">seconds.</span>
            </h2>
            <p className="text-sm md:text-base text-zinc-400 leading-relaxed">
              One prompt replaces hours of manual work. RoniDoc analyses all your client data, generates the report, and sends it automatically. No spreadsheets, no copy-paste, no delays.
            </p>
          </div>

          {/* Comparison stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-16">
            {[
              { old: '4–6 hours', new: '30 seconds', label: 'Compliance review' },
              { old: '2–3 hours', new: '15 seconds', label: 'Tax planning report' },
              { old: '45 minutes', new: '10 seconds', label: 'Multi-client query' },
            ].map((item, i) => (
              <div key={i} className="text-center px-6 py-4 border border-zinc-800/40 bg-zinc-900/20 select-none">
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-3">{item.label}</p>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-amber-400/80 line-through">{item.old}</span>
                  <ArrowRight className="h-3 w-3 text-zinc-600" />
                  <span className="text-sm text-green-400 font-medium">{item.new}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Two side-by-side mockups */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 select-none">

            {/* Left: Old way */}
            <div className="border border-zinc-800/60 bg-zinc-900/30 p-5 space-y-3 relative overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-amber-400" />
                <span className="text-xs font-medium text-amber-400">The Old Way</span>
                <span className="ml-auto text-[10px] text-amber-400/60 bg-amber-400/10 px-2 py-0.5">~4 hours</span>
              </div>

              {/* Simulated spreadsheet */}
              <div className="bg-zinc-950/60 border border-zinc-800/40 p-3 space-y-2">
                <div className="flex gap-1">
                  <div className="w-24 h-3 bg-zinc-800/60" /><div className="w-16 h-3 bg-zinc-800/40" /><div className="w-20 h-3 bg-zinc-800/50" /><div className="w-14 h-3 bg-zinc-800/40" />
                </div>
                {[1,2,3,4,5].map(r => (
                  <div key={r} className="flex gap-1 opacity-60">
                    <div className="w-24 h-2.5 bg-zinc-800/30" /><div className="w-16 h-2.5 bg-zinc-800/20" /><div className="w-20 h-2.5 bg-zinc-800/25" /><div className="w-14 h-2.5 bg-zinc-800/20" />
                  </div>
                ))}
              </div>

              {/* Manual steps */}
              <div className="space-y-2 pt-1">
                {[
                  { step: 'Download Xero reports manually', time: '20 min' },
                  { step: 'Cross-reference ATO benchmarks', time: '45 min' },
                  { step: 'Build spreadsheet analysis', time: '1.5 hrs' },
                  { step: 'Write commentary & format report', time: '1 hr' },
                  { step: 'Email report to client', time: '15 min' },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <div className="w-4 h-4 border border-zinc-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-[8px] text-zinc-600">{i + 1}</span>
                    </div>
                    <span className="text-zinc-500 flex-1">{s.step}</span>
                    <span className="text-zinc-700">{s.time}</span>
                  </div>
                ))}
              </div>

              {/* Red overlay effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-red-950/10 to-transparent pointer-events-none" />
            </div>

            {/* Right: With RoniDoc */}
            <div className="border border-[#5048e5]/20 bg-zinc-900/30 p-5 space-y-3 relative overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-green-400" />
                <span className="text-xs font-medium text-green-400">With RoniDoc AI</span>
                <span className="ml-auto text-[10px] text-green-400/80 bg-green-400/10 px-2 py-0.5">~30 seconds</span>
              </div>

              {/* Prompt */}
              <div className="bg-zinc-950/60 border border-[#5048e5]/20 p-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-[#5048e5]/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-3 w-3 text-[#7c75f0]" />
                  </div>
                  <span className="text-[11px] text-zinc-400 italic">"Run ATO compliance check for Chen & Associates and email the report"</span>
                </div>
              </div>

              {/* Animated progress steps */}
              <div className="space-y-2 pt-1">
                {[
                  { step: 'Pulling Xero P&L and Balance Sheet...', icon: FileText, done: true },
                  { step: 'Comparing against 95 ATO benchmarks...', icon: Shield, done: true },
                  { step: 'Generating branded PDF report...', icon: FileText, done: true },
                  { step: 'Sending to chen@associates.com.au...', icon: Mail, done: true },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] showcase-step-appear" style={{ animationDelay: `${i * 400}ms` }}>
                    <div className="w-4 h-4 rounded-full bg-green-400/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-2.5 w-2.5 text-green-400" />
                    </div>
                    <span className="text-zinc-300 flex-1">{s.step}</span>
                    <span className="text-green-400/60 text-[10px]">Done</span>
                  </div>
                ))}
              </div>

              {/* Result summary */}
              <div className="border-t border-[#5048e5]/20 pt-3 mt-2">
                <div className="flex items-center justify-between text-[11px]">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-green-400" />
                    <span className="text-zinc-300">Report generated & emailed</span>
                  </div>
                  <span className="text-green-400 font-medium">28s total</span>
                </div>
              </div>

              {/* Green glow */}
              <div className="absolute inset-0 bg-gradient-to-t from-green-950/10 to-transparent pointer-events-none" />
              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-green-500/5 rounded-full blur-[60px] pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .showcase-step-appear {
          animation: stepFadeIn 0.6s ease-out both;
        }
        @keyframes stepFadeIn {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes blink {
          50% { opacity: 0; }
        }
      `}</style>

      {/* ===== CAPABILITIES ===== */}
      <section id="capabilities" className="py-32 border-t border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-tl from-[rgb(24,24,27)] via-[#262635] to-purple-950/30" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[#5048e5]/8 rounded-full blur-[150px]" />
          <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-indigo-600/6 rounded-full blur-[120px]" />
        </div>
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-6 relative z-10 px-6 md:px-12 lg:px-24">Capabilities</p>
        <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-12 relative z-10 px-6 md:px-12 lg:px-24">
          90+ features. <span className="font-normal">Here are some.</span>
        </h2>

        {(() => {
          const row1 = [
            { title: 'ATO Compliance Checker', desc: 'Compare ratios against ATO industry benchmarks.' },
            { title: 'Audit Risk Predictor', desc: 'Identify which clients may attract ATO attention.' },
            { title: 'Tax Opportunity Scanner', desc: 'Find hidden deductions and write-offs automatically.' },
            { title: 'What-If Modelling', desc: 'Sixteen scenarios: assets, super, vehicles, bonuses.' },
            { title: 'Year-End Tax Planning', desc: 'Thirteen action items prioritised by deadline.' },
            { title: 'Smart Document Comparison', desc: 'Year-over-year variance with materiality flags.' },
            { title: 'Business Health Warning', desc: 'Five-factor scoring catches deterioration early.' },
            { title: 'Auto-Reconciliation', desc: 'TB vs financials, every discrepancy flagged.' },
            { title: 'Multi-Client Intelligence', desc: 'One question, answers across all clients.' },
            { title: 'Loss Carry-Back Calculator', desc: 'Model carry-back scenarios with instant tax impact.' },
            { title: 'Div 7A Loan Calculator', desc: 'Benchmark rates, minimum repayments, UPE analysis.' },
            { title: 'BAS Variance Detector', desc: 'Flag material BAS discrepancies across periods.' },
          ];
          const row2 = [
            { title: 'Client Risk Dashboard', desc: 'Traffic light scoring across your entire practice.' },
            { title: 'Xero P&L Analysis', desc: 'AI commentary on live Profit & Loss data.' },
            { title: 'Balance Sheet Insights', desc: 'Liquidity ratios and working capital analysis.' },
            { title: 'Aged Receivables Alerts', desc: 'Flag overdue invoices and cash flow risks.' },
            { title: 'Document Q&A', desc: 'Ask natural language questions across all uploads.' },
            { title: 'Branded Report Builder', desc: 'PDF, Word, Excel exports with your firm logo.' },
            { title: 'Industry Benchmarking', desc: '95 ANZSIC codes with ATO ratio analysis.' },
            { title: 'CGT Event Modelling', desc: 'Capital gains scenarios with discount method.' },
            { title: 'Depreciation Optimizer', desc: 'Identify assets eligible for instant write-off.' },
            { title: 'FBT Exposure Scanner', desc: 'Detect potential fringe benefits tax liabilities.' },
            { title: 'Trust Distribution Planner', desc: 'Model distributions across beneficiaries.' },
            { title: 'Deadline & Lodgement Tracker', desc: 'Never miss a due date across all clients.' },
          ];
          const renderCard = (cap: { title: string; desc: string }, i: number) => (
            <div key={i} className="flex-shrink-0 w-[280px] md:w-[320px] h-[100px] bg-[#262635]/80 backdrop-blur-sm border border-zinc-800/40 p-6 group hover:bg-[#5048e5]/8 hover:border-[#5048e5]/20 transition-all duration-500 flex flex-col justify-center">
              <h3 className="text-[14px] font-medium mb-2 group-hover:text-[#7c75f0] transition-colors duration-300">{cap.title}</h3>
              <p className="text-[12px] text-zinc-600 leading-relaxed group-hover:text-zinc-400 transition-colors duration-300">{cap.desc}</p>
            </div>
          );
          return (
            <div className="relative z-10 space-y-3 overflow-hidden">
              {/* Row 1 */}
              <div className="capability-ticker hover:[animation-play-state:paused]" style={{ animationDuration: '60s' }}>
                <div className="capability-ticker-inner">
                  {row1.map(renderCard)}
                </div>
                <div className="capability-ticker-inner" aria-hidden>
                  {row1.map(renderCard)}
                </div>
              </div>
              {/* Row 2 */}
              <div className="capability-ticker capability-ticker-reverse hover:[animation-play-state:paused]" style={{ animationDuration: '70s' }}>
                <div className="capability-ticker-inner">
                  {row2.map(renderCard)}
                </div>
                <div className="capability-ticker-inner" aria-hidden>
                  {row2.map(renderCard)}
                </div>
              </div>
            </div>
          );
        })()}

        <style>{`
          .capability-ticker {
            display: flex;
            width: max-content;
            animation: tickerLeft linear infinite;
          }
          .capability-ticker-reverse {
            animation-direction: reverse;
          }
          .capability-ticker-inner {
            display: flex;
            gap: 12px;
            padding-right: 12px;
          }
          @keyframes tickerLeft {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </section>

      {/* ===== NUMBERS ===== */}
      <section id="numbers" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-16">By The Numbers</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-16">
          {[
            { number: '90+', label: 'AI features', sub: 'built and deployed' },
            { number: '95', label: 'industry benchmarks', sub: 'from the ATO' },
            { number: '16', label: 'tax scenarios', sub: 'with real calculations' },
            { number: '8', label: 'report templates', sub: 'auto-generated' },
          ].map((stat) => (
            <div key={stat.label} className="group">
              <span className="text-[clamp(2.5rem,5vw,4.5rem)] font-extralight text-white group-hover:text-[#7c75f0] transition-colors duration-500 block leading-none">
                {stat.number}
              </span>
              <p className="text-sm text-zinc-300 mt-3">{stat.label}</p>
              <p className="text-xs text-zinc-700 mt-1">{stat.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section id="testimonials" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-16">What They Say</p>
        <div className="space-y-20">
          {[
            { quote: 'RoniDoc fundamentally changed how our team approaches client advisory. The intelligence it surfaces from routine data is remarkable. Things we would have missed entirely.', name: 'Sarah Chen', role: 'Partner, Chen & Associates, Melbourne' },
            { quote: 'We identified over $200,000 in missed deductions across our client base in the first month. The tax opportunity scanner alone pays for the entire subscription.', name: 'Michael Torres', role: 'Senior Tax Advisor, Torres Advisory, Sydney' },
            { quote: 'The ability to ask one question and get answers across all 200 clients simultaneously is something no other platform offers. It has changed our practice entirely.', name: 'Priya Sharma', role: 'Practice Manager, Sharma & Partners, Brisbane' },
          ].map((t, i) => (
            <blockquote key={i} className="border-l border-zinc-800 pl-8 md:pl-16 max-w-4xl">
              <p className="text-[clamp(1.1rem,2.5vw,1.7rem)] font-extralight text-zinc-300 leading-relaxed italic mb-8">
                "{t.quote}"
              </p>
              <footer>
                <p className="text-sm font-medium">{t.name}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{t.role}</p>
              </footer>
            </blockquote>
          ))}
        </div>
      </section>

      {/* ===== WHO IT'S FOR ===== */}
      <section id="who-its-for" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">Who It's For</p>
        <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-16">
          Purpose-built for <span className="font-normal">Australian accounting professionals.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Building2, title: 'Small to Mid-Tier Firms', tag: '10–500 clients', desc: 'Firms managing 10–500+ clients who want to deliver advisory services without hiring more staff. Automate compliance work and focus on what matters.' },
            { icon: Briefcase, title: 'Practice Managers', tag: 'Firm-wide visibility', desc: 'Oversee firm-wide compliance, identify at-risk clients, and generate reports across your entire practice from a single dashboard.' },
            { icon: Calculator, title: 'Tax Advisors & Accountants', tag: 'Data-driven advice', desc: 'Surface tax opportunities, model scenarios, and deliver year-end planning, all backed by real ATO benchmarks and live Xero data.' },
          ].map((persona, i) => (
            <div key={i} className="group border border-zinc-800/60 p-8 hover:border-[#5048e5]/20 transition-all duration-500">
              <persona.icon className="h-8 w-8 text-[#c4b5fd] mb-5 group-hover:text-[#7c75f0] transition-colors duration-500" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#7c75f0]/40 mb-3">{persona.tag}</p>
              <h3 className="text-lg font-medium mb-3">{persona.title}</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">{persona.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SECURITY / COMPLIANCE ===== */}
      <section id="security" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">Security & Compliance</p>
            <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-6">
              Your client data deserves <span className="font-normal">bank-grade protection.</span>
            </h2>
            <p className="text-sm text-zinc-500 leading-relaxed mb-10">
              We built RoniDoc with the same security standards expected of the firms using it. Every layer, from encryption to access control, is designed for sensitive financial data.
            </p>
            <div className="space-y-6">
              {[
                { icon: Lock, title: 'AES-256 Encryption', desc: 'All data encrypted at rest and in transit. API keys, OAuth tokens, and credentials use AES-256-CBC with per-record initialisation vectors.' },
                { icon: Shield, title: 'Tenant Isolation', desc: 'Every firm operates in a fully isolated environment. Row-level security ensures no cross-tenant data access, ever.' },
                { icon: Eye, title: 'Read-Only Xero Access', desc: 'Our Xero integration is strictly read-only. We pull reports and contacts but cannot modify, delete, or write to your Xero data.' },
                { icon: Server, title: 'AWS Infrastructure', desc: 'Hosted on AWS with encrypted RDS databases, Lambda compute, and CloudFront CDN. All data stays in controlled infrastructure.' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#5048e5]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon className="h-4 w-4 text-[#7c75f0]" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-1">{item.title}</h4>
                    <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative w-72 h-72">
              {/* Outer ring + Lock icon */}
              <div className="absolute inset-0 rounded-full border border-zinc-800/40 sec-ring" style={{ animationDuration: '20s' }}>
                <div className="absolute -top-[18px] left-1/2 -ml-[18px] w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center sec-icon" style={{ animationDuration: '20s' }}>
                  <Lock className="h-3.5 w-3.5 text-green-400" />
                </div>
              </div>

              {/* Middle ring + Eye icon */}
              <div className="absolute inset-8 rounded-full border border-[#5048e5]/15 sec-ring" style={{ animationDuration: '16s' }}>
                <div className="absolute top-1/2 -mt-[18px] -right-[18px] w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center sec-icon" style={{ animationDuration: '16s' }}>
                  <Eye className="h-3.5 w-3.5 text-amber-400" />
                </div>
              </div>

              {/* Inner ring + Server icon */}
              <div className="absolute inset-16 rounded-full border border-[#5048e5]/25 sec-ring" style={{ animationDuration: '12s' }}>
                <div className="absolute -bottom-[18px] left-1/2 -ml-[18px] w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center sec-icon" style={{ animationDuration: '12s' }}>
                  <Server className="h-3.5 w-3.5 text-blue-400" />
                </div>
              </div>

              {/* Center shield */}
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="w-20 h-20 rounded-full bg-[#5048e5]/10 flex items-center justify-center">
                  <Shield className="h-8 w-8 text-[#7c75f0]" />
                </div>
              </div>

              {/* Glow */}
              <div className="absolute inset-0 rounded-full bg-[#5048e5]/5 blur-[60px] pointer-events-none" />
            </div>
          </div>

          <style>{`
            .sec-ring {
              animation: secSpin linear infinite;
            }
            .sec-icon {
              animation: secCounterSpin linear infinite;
            }
            @keyframes secSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes secCounterSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(-360deg); }
            }
          `}</style>
        </div>
      </section>

      {/* ===== INTEGRATIONS / TRUST BAR ===== */}
      <section className="px-6 md:px-12 lg:px-24 py-20 border-y border-zinc-800/60 bg-[#16161e]">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-10 text-center">Integrations & Technology</p>
        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
          {[
            { name: 'Xero', color: '#13B5EA' },
            { name: 'ATO', color: '#1B6FA5' },
            { name: 'OpenAI', color: '#10A37F' },
            { name: 'Anthropic', color: '#D4A373' },
            { name: 'Google AI', color: '#4285F4' },
            { name: 'xAI', color: '#FFFFFF' },
            { name: 'Google Drive', color: '#34A853' },
            { name: 'OneDrive', color: '#0078D4' },
            { name: 'AWS', color: '#FF9900' },
          ].map((brand) => (
            <div key={brand.name} className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-opacity duration-300">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: brand.color }} />
              <span className="text-xs tracking-[0.1em] uppercase text-zinc-200 font-medium">{brand.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">FAQ</p>
        <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-16">
          Common <span className="font-normal">questions.</span>
        </h2>
        {(() => {
          const faqs = [
            { q: 'What data do you connect to?', a: 'RoniDoc connects to Xero via official OAuth2 integration to pull P&L, Balance Sheet, Trial Balance, and Aged Reports. You can also upload any PDF, spreadsheet, or document: tax returns, financial statements, engagement letters, and more.' },
            { q: 'Is my client data secure?', a: 'Yes. All data is encrypted with AES-256 at rest and in transit. Each tenant is fully isolated. No firm can access another\'s data. We use AWS infrastructure with enterprise-grade compliance. Your Xero connection is read-only.' },
            { q: 'How does the AI work?', a: 'RoniDoc uses a combination of leading AI models (Anthropic Claude, OpenAI GPT-4, Google Gemini, xAI Grok) to understand your documents and financial data. Each query searches across all your data to provide answers with citations.' },
            { q: 'Can I try it before committing?', a: 'Absolutely. Our 1 month free trial includes 3 clients, 50 AI queries, and full access to all features. No credit card required. Upgrade to a paid plan at any time.' },
            { q: 'What makes this different?', a: 'Most tools just display data. RoniDoc connects your documents, Xero financials, and ATO benchmarks into a single AI layer. Ask questions across all clients simultaneously, auto-detect compliance issues, and generate branded reports.' },
            { q: 'Do you support multiple Xero organisations?', a: 'Yes. Each client can be connected to their own Xero organisation. ABN auto-matching links clients to their Xero data instantly. Manage hundreds of connections from one dashboard.' },
            { q: 'Can I export reports?', a: 'Yes. Reports can be exported as PDF, Word, CSV, and Excel. All exports are branded with your firm\'s details and include charts, tables, and AI-generated commentary.' },
            { q: 'What cloud storage do you support?', a: 'RoniDoc integrates with Google Drive and OneDrive for automatic document syncing. Connect your cloud folders and new documents are indexed automatically as they\'re added.' },
          ];
          const left = faqs.slice(0, 4);
          const right = faqs.slice(4);
          const renderFaq = (item: typeof faqs[0], i: number) => (
            <details key={i} className="group border-b border-zinc-800/60">
              <summary className="flex items-center justify-between py-6 cursor-pointer list-none">
                <span className="text-sm font-medium pr-4">{item.q}</span>
                <ChevronDown className="h-4 w-4 text-zinc-600 group-open:rotate-180 transition-transform duration-300 flex-shrink-0" />
              </summary>
              <p className="text-sm text-zinc-500 leading-relaxed pb-6 pr-8">{item.a}</p>
            </details>
          );
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
              <div>{left.map(renderFaq)}</div>
              <div>{right.map((item, i) => renderFaq(item, i + 4))}</div>
            </div>
          );
        })()}
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-8">Pricing</p>
        <h2 className="text-[clamp(1.5rem,3.5vw,3rem)] font-extralight mb-20">
          Start free. <span className="font-normal">Scale when you're ready.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-[1px] bg-zinc-900/80">
          {[
            { name: 'Starter', price: '$99', clients: '10 clients', queries: '500 queries/mo', pop: false },
            { name: 'Professional', price: '$299', clients: '50 clients', queries: '2,000 queries/mo', pop: true },
            { name: 'Enterprise', price: '$599', clients: '200 clients', queries: '10,000 queries/mo', pop: false },
            { name: 'Scale', price: '$999', clients: '500 clients', queries: '50,000 queries/mo', pop: false },
          ].map((plan) => (
            <div key={plan.name} className={cn('bg-[#262635] p-8 md:p-10 flex flex-col', plan.pop && 'ring-1 ring-[#5048e5]/20')}>
              {plan.pop && <p className="text-[10px] tracking-[0.3em] uppercase text-[#7c75f0] mb-6">Most Popular</p>}
              <h3 className="text-lg font-medium">{plan.name}</h3>
              <div className="my-4">
                <span className="text-4xl font-extralight">{plan.price}</span>
                <span className="text-zinc-600 text-sm">/mo</span>
              </div>
              <p className="text-xs text-zinc-500 mb-1">{plan.clients}</p>
              <p className="text-xs text-zinc-500 mb-8">{plan.queries}</p>
              <Link to="/signup" className={cn(
                'mt-auto block text-center text-[12px] tracking-[0.15em] uppercase py-3.5 rounded-full transition-all duration-300',
                plan.pop ? 'bg-[#5048e5] hover:bg-[#5f58e8] text-white' : 'border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white'
              )}>
                Get Started
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center mt-10">
          <Link to="/signup" className="text-[13px] text-zinc-600 hover:text-[#7c75f0] transition">
            1 month free trial, no credit card required →
          </Link>
        </p>
      </section>

      {/* ===== CONTACT / CTA ===== */}
      <section id="contact" className="px-6 md:px-12 lg:px-24 py-32 border-t border-zinc-900">
        <div className="max-w-3xl">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-10">Get In Touch</p>
          <h2 className="text-[clamp(2rem,5vw,4rem)] font-extralight leading-tight mb-10">
            Ready to see what your
            <br /><span className="font-normal">data already knows?</span>
          </h2>
          <Link to="/signup"
            className="inline-flex items-center gap-3 text-[13px] tracking-[0.15em] uppercase text-[#7c75f0] hover:text-white transition-colors duration-300 group">
            Start your free trial
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <div className="mt-16 pt-8 border-t border-zinc-900 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-600 mb-2">Email</p>
              <a href="mailto:hello@ronidoc.ai" className="text-sm text-zinc-400 hover:text-white transition">hello@ronidoc.ai</a>
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-600 mb-2">Location</p>
              <p className="text-sm text-zinc-400">Adelaide, Australia</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="px-6 md:px-12 lg:px-24 py-10 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#7c75f0]" />
          <span className="text-xs tracking-[0.15em] font-light text-zinc-600">RONIDOC<span className="font-semibold">AI</span></span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/terms" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Terms</Link>
          <Link to="/privacy-policy" className="text-[11px] text-zinc-700 hover:text-zinc-400 transition">Privacy</Link>
        </div>
        <p className="text-[11px] text-zinc-800">&copy; {new Date().getFullYear()} RoniDoc AI</p>
      </footer>
    </div>
  );
}
